import type { ProfileDetail } from '@teentack/shared';
import { CheckIcon } from '@/components/ui/Icons';
import { requirementsFor } from './profile-display';

export function CompletenessCard({ profile }: { profile: ProfileDetail }) {
  const requirements = requirementsFor(profile);
  const done = requirements.filter((entry) => entry.done).length;
  const percent = Math.round((done / requirements.length) * 100);
  const complete = done === requirements.length;

  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-soft">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-bold text-ink">Profile strength</h2>
        <span className={`text-sm font-extrabold ${complete ? 'text-safe-600' : 'text-brand-600'}`}>
          {percent}%
        </span>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ease-out ${
            complete ? 'bg-safe-500' : 'bg-brand-gradient'
          }`}
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Profile completeness"
        />
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        {complete
          ? 'Everything is filled in. Job posters see the full picture.'
          : `${requirements.length - done} thing${requirements.length - done === 1 ? '' : 's'} left to add.`}
      </p>

      <ul className="mt-4 space-y-2">
        {requirements.map((entry) => (
          <li key={entry.label} className="flex items-center gap-2.5 text-sm">
            <span
              className={`grid size-4.5 shrink-0 place-items-center rounded-full ${
                entry.done ? 'bg-safe-100 text-safe-700' : 'border border-dashed border-slate-300'
              }`}
            >
              {entry.done && <CheckIcon className="size-3" />}
            </span>
            <span className={entry.done ? 'text-slate-500 line-through' : 'font-medium text-ink'}>
              {entry.label}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
